import React from 'react';
import { Row, Col, Dropdown } from 'react-bootstrap';
import moment from 'moment';
import { modal } from '../modal/ModalEmitter';
import { currencyFormat, extractTimestamp } from '../../util/general';
import CurrencyItem from '../form/misc/CurrencyItem';
import PaymentCashRefundForm from './PaymentCashRefundForm';
import ApiService from '../../services/ApiService';
import { CashTransaction } from './types';
import CashFlow from './CashFlow';

interface Props {
  maxRefundAmount?: number;
  onRefund: ({ saleTx, refundTx }: { saleTx: CashTransaction; refundTx: CashTransaction }) => void;
  readOnly?: boolean;
  sessionId?: string;
  showRefundForm?: boolean;
  transaction: CashTransaction;
  userInitials?: string;
}

const PaymentCashTransaction = ({
  maxRefundAmount,
  onRefund,
  readOnly = false,
  sessionId,
  showRefundForm = false,
  transaction,
  userInitials,
}: Props) => {
  const modalOptions = {
    staticBackdrop: true,
    hidePrint: true,
    hideClose: true,
    headerClass: 'bg-warning text-light',
  };

  const doRefund = (amount: Number) => {
    if (!transaction) {
      return;
    }

    ApiService.put(`/payment/cash/${transaction.id}/refund`, { amount, userInitials, sessionId })
      .then(response => {
        if (onRefund) {
          onRefund({ saleTx: response.data.sale, refundTx: response.data.refund });
        }
      })
      .catch(err => {
        console.error(err);
      });
  };

  const handleRefund = (amount: number) => {
    modal.confirm({
      ...modalOptions,
      size: 'md',
      title: 'Return Cash To Customer',
      message: (
        <>
          <CashFlow direction="left" />
          <h4 className="text-center">
            Return <CurrencyItem>{amount}</CurrencyItem>
          </h4>
        </>
      ),
      callback: () => {
        doRefund(amount);
      },
    });
  };

  const handlePartialRefund = () => {
    modal.open({
      title: 'Select Cash Refund Amount',
      size: 'md',
      hideFooter: true,
      hidePrint: true,
      style: 'center',
      staticBackdrop: true,
      headerClass: 'primary',
      message: (
        <PaymentCashRefundForm
          maxAmount={transaction.amount}
          onSubmit={amount => {
            modal.close();
            handleRefund(parseFloat(amount));
          }}
        />
      ),
    });
  };

  const getMaxRefundAmount = () => {
    const refundAbleAmount = transaction.amount;
    if (maxRefundAmount !== undefined) {
      // return Math.min(refundAbleAmount, maxRefundAmount);
      // allow a cash refund over the original transaction amount
      return maxRefundAmount;
    } else {
      return refundAbleAmount;
    }
  };

  const renderRefunds = () => {
    if (!transaction.refunds || !transaction.refunds.length) {
      return null;
    }

    return (
      <Row>
        <Col>
          {transaction.refunds.map((refund, i) => {
            return (
              <div key={i}>
                {moment(extractTimestamp(refund.id)).format('M/D/YY')} Refund <i className="fas fa-arrow-right" />{' '}
                {currencyFormat(refund.amount)}
              </div>
            );
          })}
        </Col>
      </Row>
    );
  };

  return (
    <>
      <Row>
        <Col xs="auto">
          <div style={{ width: '92px' }}>
            <i className="fad fa-money-bill" style={{ color: 'green', fontSize: '40px' }} />
          </div>
        </Col>
        <Col>
          <div className="d-inline-block" style={{ fontSize: '19px' }}>
            <span className="text-bold mr-2">{currencyFormat(transaction.amount)}</span>
            <span className="text-uppercase">Cash</span>
          </div>
        </Col>
        <Col className="text-right">
          {!showRefundForm && !readOnly && transaction.amount > 0 && (
            <Dropdown className="no-caret" alignRight>
              <Dropdown.Toggle variant="secondary">
                <i className="fas fa-cog" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handlePartialRefund} className="text-danger text-bold">
                  Partial Refund
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => handleRefund(transaction.amount)} className="text-danger text-bold">
                  Full Refund
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )}
        </Col>
      </Row>
      {renderRefunds()}
      {showRefundForm && getMaxRefundAmount() > 0 && (
        <>
          <hr />
          <PaymentCashRefundForm
            maxAmount={getMaxRefundAmount()}
            onSubmit={amount => {
              handleRefund(parseFloat(amount));
            }}
          />
        </>
      )}
    </>
  );
};

export default PaymentCashTransaction;
